import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './shared/hooks/useAuth.jsx';
import { ProtectedRoute } from './shared/components/ProtectedRoute.jsx';
import { ErrorBoundary } from './shared/components/ErrorBoundary.jsx';
import { CheckpointProvider } from './modules/checkpoint/CheckpointContext.jsx';

const LoginPage        = lazy(() => import('./modules/auth/LoginPage.jsx'));
const StaffLoginPage   = lazy(() => import('./modules/auth/StaffLoginPage.jsx'));
const ProfilePage      = lazy(() => import('./modules/auth/ProfilePage.jsx'));
const ConfirmPage      = lazy(() => import('./modules/auth/ConfirmPage.jsx'));
const SetupPage        = lazy(() => import('./modules/auth/SetupPage.jsx'));
const CheckinStatusPage = lazy(() => import('./modules/checkin/CheckinStatusPage.jsx'));
const CheckpointOverlay = lazy(() => import('./modules/checkpoint/CheckpointOverlay.jsx'));
const CheckpointBanner  = lazy(() => import('./modules/checkpoint/CheckpointBanner.jsx'));
const CheckinDashboard  = lazy(() => import('./admin/CheckinDashboard.jsx'));
const CheckpointControl = lazy(() => import('./admin/CheckpointControl.jsx'));
const QuestionPool      = lazy(() => import('./admin/QuestionPool.jsx'));
const QuestionManager   = lazy(() => import('./admin/QuestionManager.jsx'));
const CheckinWindowControl  = lazy(() => import('./owner/CheckinWindowControl.jsx'));
const TestControl           = lazy(() => import('./owner/TestControl.jsx'));
const DocumentUploadPage    = lazy(() => import('./owner/DocumentUploadPage.jsx'));
const ChatPage         = lazy(() => import('./modules/chatbot/ChatPage.jsx'));
const AdminChatPage       = lazy(() => import('./admin/AdminChatPage.jsx'));
const TestQuestionPool    = lazy(() => import('./admin/TestQuestionPool.jsx'));
const TestPage         = lazy(() => import('./modules/test/TestPage.jsx'));
const ResultPage       = lazy(() => import('./modules/test/ResultPage.jsx'));
const CertificatePage  = lazy(() => import('./modules/test/CertificatePage.jsx'));
const PrivacyPage      = lazy(() => import('./modules/privacy/PrivacyPage.jsx'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function UnauthorizedPage() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-sm w-full text-center">
        <div className="text-4xl mb-4">🔒</div>
        <h1 className="text-xl font-bold mb-2">Hozzáférés megtagadva</h1>
        <p className="text-gray-600 text-sm mb-4">Nincs jogosultsága ehhez az oldalhoz.</p>
        <a href="/" className="btn-primary w-full block">Vissza a főoldalra</a>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <CheckpointProvider>
          <Suspense fallback={null}>
            <CheckpointBanner />
            <CheckpointOverlay />
          </Suspense>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login"        element={<LoginPage />} />
              <Route path="/beallitas"    element={<SetupPage />} />
              <Route path="/staff-login"  element={<StaffLoginPage />} />
              <Route path="/adatkezeles"  element={<PrivacyPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              <Route path="/profil" element={<ProtectedRoute role="user"><ProfilePage /></ProtectedRoute>} />
              <Route path="/adatok" element={<ProtectedRoute role="user"><ConfirmPage /></ProtectedRoute>} />
              <Route path="/checkin" element={<ProtectedRoute role="user"><CheckinStatusPage /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute role="user"><ChatPage /></ProtectedRoute>} />
              <Route path="/teszt" element={<ProtectedRoute role="user"><TestPage /></ProtectedRoute>} />
              <Route path="/teszt/eredmeny" element={<ProtectedRoute role="user"><ResultPage /></ProtectedRoute>} />
              <Route path="/teszt/tanusitvany" element={<ProtectedRoute role="user"><CertificatePage /></ProtectedRoute>} />

              <Route path="/admin/chat"       element={<ProtectedRoute role="admin"><AdminChatPage /></ProtectedRoute>} />
              <Route path="/admin/checkin"    element={<ProtectedRoute role="admin"><CheckinDashboard /></ProtectedRoute>} />
              <Route path="/admin/checkpoint" element={<ProtectedRoute role="admin"><CheckpointControl /></ProtectedRoute>} />
              <Route path="/admin/tesztkerdések" element={<ProtectedRoute role="admin"><TestQuestionPool /></ProtectedRoute>} />
              <Route path="/admin/eszkozok"   element={<ProtectedRoute role="admin"><QuestionManager /></ProtectedRoute>} />

              <Route path="/owner/ablak"  element={<ProtectedRoute role="owner"><CheckinWindowControl /></ProtectedRoute>} />
              <Route path="/owner/teszt"  element={<ProtectedRoute role="owner"><TestControl /></ProtectedRoute>} />
              <Route path="/feltoltes"    element={<DocumentUploadPage />} />

              <Route path="/"  element={<Navigate to="/login" replace />} />
              <Route path="*"  element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
          </CheckpointProvider>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
